'use strict';

import urljoin from 'url-join';
import { getNavigableDiscoveryEntries } from '@/features/discoveryPanels/useDiscoveryPanelEntries';
import { PROJECT_TOOL_GROUP, PROJECT_TOOL_IDS } from './projectToolTypes';

/**
 * @typedef {import('./projectToolTypes').ProjectToolKind} ProjectToolKind
 */

/**
 * Canonical project tool (surface-agnostic). No React icons — adapters attach MUI/Lucide.
 *
 * @typedef {Object} ProjectTool
 * @property {string} id
 * @property {ProjectToolKind} kind
 * @property {string} label
 * @property {string} [description]
 * @property {string} [path] — in-app path when navigable
 * @property {string} [externalHref] — e.g. IBP URL
 * @property {boolean} [external]
 * @property {string} [productType] — discovery panel product type
 * @property {string} [group]
 * @property {boolean} [landingNeedsProject] — landing: open project picker when no projectCode
 * @property {ProjectTool[]} [children]
 */

/**
 * @typedef {Object} BuildProjectToolsContext
 * @property {'landing'|'inProject'} variant
 * @property {boolean} hasDiscoveryPanels
 * @property {Array<{ id: string; productType: string; title: string; path: string|null }>} discoveryEntries
 * @property {string|null|undefined} projectCode
 * @property {string|null|undefined} projectBasePath
 * @property {boolean} [hasSpectralData]
 * @property {boolean} biovizStatus
 * @property {boolean} biovizLoading
 * @property {string|null|undefined} biovizHref
 * @property {string} ibpFallbackHref
 */

/**
 * Deterministic canonical project tools for landing and in-project surfaces.
 * Discovery navigable rows always come from getNavigableDiscoveryEntries(discoveryEntries).
 *
 * @param {BuildProjectToolsContext} ctx
 * @returns {ProjectTool[]}
 */
export function buildProjectTools(ctx) {
	const {
		variant,
		hasDiscoveryPanels,
		discoveryEntries,
		projectCode,
		projectBasePath,
		hasSpectralData = false,
		biovizStatus,
		biovizLoading,
		biovizHref,
		ibpFallbackHref,
	} = ctx;

	if (hasDiscoveryPanels) {
		const tools = [];
		const homePath =
			variant === 'inProject' && projectBasePath
				? urljoin(projectBasePath, 'home')
				: projectCode
					? urljoin('/home/project', projectCode, 'home')
					: null;

		tools.push({
			id: PROJECT_TOOL_IDS.HOME,
			kind: 'home',
			label: 'Home',
			path: homePath,
			group: PROJECT_TOOL_GROUP.PRIMARY,
			landingNeedsProject: variant === 'landing' && !projectCode,
		});

		for (const e of getNavigableDiscoveryEntries(discoveryEntries)) {
			tools.push({
				id: e.id,
				kind: 'discovery',
				label: e.title,
				path: e.path,
				productType: e.productType,
				group: PROJECT_TOOL_GROUP.PRIMARY,
			});
		}
		return tools;
	}

	const tools = [];
	const landingBase = projectCode ? urljoin('/home/project', projectCode) : null;
	const inBase = projectBasePath || null;

	const homePath =
		variant === 'inProject' && inBase ? urljoin(inBase, 'home') : landingBase ? urljoin(landingBase, 'home') : null;

	tools.push({
		id: PROJECT_TOOL_IDS.HOME,
		kind: 'home',
		label: 'Home',
		path: homePath,
		group: PROJECT_TOOL_GROUP.PRIMARY,
		landingNeedsProject: variant === 'landing' && !projectCode,
	});

	if (variant === 'inProject' && inBase) {
		const resultsPath = urljoin(inBase, 'results');
		if (hasSpectralData) {
			tools.push({
				id: PROJECT_TOOL_IDS.RESULTS,
				kind: 'files',
				label: 'Results',
				path: resultsPath,
				group: PROJECT_TOOL_GROUP.PRIMARY,
				children: [
					{
						id: PROJECT_TOOL_IDS.RESULTS_DELIVERABLES,
						kind: 'files',
						label: 'Deliverables',
						path: urljoin(inBase, 'results'),
						group: PROJECT_TOOL_GROUP.PRIMARY,
					},
					{
						id: PROJECT_TOOL_IDS.RESULTS_SPECTRAL,
						kind: 'files',
						label: 'Spectral Data',
						path: urljoin(inBase, 'spectral-data'),
						group: PROJECT_TOOL_GROUP.PRIMARY,
					},
				],
			});
		} else {
			tools.push({
				id: PROJECT_TOOL_IDS.RESULTS,
				kind: 'files',
				label: 'Results',
				path: resultsPath,
				group: PROJECT_TOOL_GROUP.PRIMARY,
			});
		}
		tools.push({
			id: PROJECT_TOOL_IDS.STATS,
			kind: 'analysis',
			label: 'Stats Table',
			path: urljoin(inBase, 'statstable'),
			group: PROJECT_TOOL_GROUP.PRIMARY,
		});
		tools.push({
			id: PROJECT_TOOL_IDS.PATHWAYS,
			kind: 'analysis',
			label: 'Pathway Explorer',
			path: urljoin(inBase, 'pathwayexplorer'),
			group: PROJECT_TOOL_GROUP.PRIMARY,
		});
	} else {
		tools.push({
			id: PROJECT_TOOL_IDS.RESULTS,
			kind: 'files',
			label: 'Download Results',
			path: landingBase ? urljoin(landingBase, 'results') : null,
			group: PROJECT_TOOL_GROUP.PRIMARY,
			landingNeedsProject: variant === 'landing' && !projectCode,
		});
		tools.push({
			id: PROJECT_TOOL_IDS.PATHWAYS,
			kind: 'analysis',
			label: 'Pathway Explorer',
			path: landingBase ? urljoin(landingBase, 'pathwayexplorer') : null,
			group: PROJECT_TOOL_GROUP.PRIMARY,
			landingNeedsProject: variant === 'landing' && !projectCode,
		});
		tools.push({
			id: PROJECT_TOOL_IDS.STATS,
			kind: 'analysis',
			label: 'Statistics Table',
			path: landingBase ? urljoin(landingBase, 'statstable') : null,
			group: PROJECT_TOOL_GROUP.PRIMARY,
			landingNeedsProject: variant === 'landing' && !projectCode,
		});
	}

	const ibpHrefResolved = biovizHref || ibpFallbackHref;
	if (biovizStatus && !biovizLoading) {
		tools.push({
			id: PROJECT_TOOL_IDS.IBP,
			kind: 'external',
			label: variant === 'landing' ? 'Bioinformatics Platform' : 'Bioinformatics Tools',
			externalHref: ibpHrefResolved,
			external: true,
			group: PROJECT_TOOL_GROUP.SECONDARY,
		});
	}

	return tools;
}
